import React, { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import ReviewItemList from './ReviewItemList'
import ReviewDetailPanel from './ReviewDetailPanel'

const REFRESH_INTERVAL_MS = 60 * 1000

const ReviewTab = ({ url, onViewInQueue }) => {
  const [items, setItems] = useState([])
  const [counts, setCounts] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [hiddenCategories, setHiddenCategories] = useState(new Set())
  const intervalRef = useRef(null)
  const selectedItemRef = useRef(null)

  useEffect(() => { selectedItemRef.current = selectedItem }, [selectedItem])

  const fetchReviewItems = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError('')
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/review-items`)
      const data = resp.data
      setItems(data.items || [])
      setCounts(data.counts || {})
      // Auto-select first item if none selected (use ref to avoid stale closure)
      if (!selectedItemRef.current && data.items?.length > 0) {
        setSelectedItem(data.items[0])
      }
    } catch (err) {
      console.error('Error fetching review items:', err)
      if (!silent) setError('Failed to load review items')
    }
    setLoading(false)
  }, [url])

  useEffect(() => {
    fetchReviewItems()
    intervalRef.current = setInterval(() => fetchReviewItems(true), REFRESH_INTERVAL_MS)
    return () => clearInterval(intervalRef.current)
  }, [fetchReviewItems])

  const handleToggleCategory = useCallback((category) => {
    setHiddenCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    setHiddenCategories(prev => {
      if (prev.size >= 5) return new Set() // all hidden → show all
      return new Set(['needs_review', 'aspire_tickets', 'field_issues', 'stale', 'unread_messages'])
    })
  }, [])

  const handleItemCompleted = useCallback((completedItem) => {
    // Optimistic removal
    setItems(prev => prev.filter(i => !(i.scheduleItemId === completedItem.scheduleItemId && i.category === completedItem.category)))
    setCounts(prev => ({
      ...prev,
      [completedItem.category]: Math.max(0, (prev[completedItem.category] || 0) - 1),
      total: Math.max(0, (prev.total || 0) - 1)
    }))
    setSelectedItem(null)
  }, [])

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <ReviewItemList
        items={items}
        counts={counts}
        loading={loading}
        error={error}
        selectedItem={selectedItem}
        hiddenCategories={hiddenCategories}
        onSelect={setSelectedItem}
        onToggleCategory={handleToggleCategory}
        onToggleAll={handleToggleAll}
        onRetry={() => fetchReviewItems()}
      />
      <ReviewDetailPanel
        item={selectedItem}
        url={url}
        onViewInQueue={onViewInQueue}
        onItemCompleted={handleItemCompleted}
      />
    </div>
  )
}

export default ReviewTab
